import axios from "axios";
import { Copy, Download, LoaderIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import premium from "@/assets/premium-badge.svg";
import gold from "@/assets/gold-badge.svg";
import platinum from "@/assets/platinum.svg";

const ViewGiftCode = ({ activeTab }) => {
    const [giftResponse, setGiftResponse] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchGiftCodes = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`/user/inventory`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            setGiftResponse(response.data.gift_subscriptions || []);
        } catch (error) {
            console.error('Error fetching gift codes:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (activeTab === 'viewGiftCodes') {
            fetchGiftCodes();
        }
    }, [activeTab]);

    const handleCopy = async (url) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                // Fallback for mobile devices and older browsers
                const textArea = document.createElement('textarea');
                textArea.value = url;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                textArea.remove();
            }
            toast.success("Gift Code Copied to Clipboard!", { id: "gift-code-copy", duration: 2000 });
        } catch (err) {
            toast.error("Failed to Copy Gift Code!", { id: "gift-code-copy", duration: 2000 });
        }
    };

    const getPlanBadge = (planType) => {
        switch (planType?.toLowerCase()) {
            case 'premium': return premium;
            case 'gold': return gold;
            case 'platinum': return platinum;
            default: return premium;
        }
    };

    const handleDownload = useCallback((giftCode, planType) => {
        toast.loading("Downloading Gift Code...", { id: "gift-code-download" });
        const url = `https://soundofmeme.com/redeem/success/${giftCode}`;
        const messageContent = `
=================================================
           🎁 SoundOfMeme Gift Code 🎁
=================================================

Thank you for choosing SoundOfMeme!

Your Gift Code: ${giftCode}

Instructions:
1. Visit ${url}
2. Click on "Redeem Gift"
3. Start enjoying your ${planType} subscription!

=================================================
Questions? Contact support@soundofmeme.com
=================================================
`;
        const element = document.createElement('a');
        const file = new Blob([messageContent], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'soundofmeme-gift-code.txt';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        toast.dismiss("gift-code-download");
    }, []);

    return (
        <div className="w-full mt-4 space-y-3">
            <h3 className="sm:text-sm text-xs font-semibold text-white mb-4 px-1">Share these gift codes with your loved ones and surprise them with a subscription!</h3>
            {loading ? (
                <div className="flex justify-center py-6">
                    <LoaderIcon className="w-8 h-8 text-green-400 animate-spin" />
                </div>
            ) : giftResponse.length === 0 ? (
                <div className="text-center py-6 text-slate-400">
                    No gift codes found in your inventory
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-x-1 gap-y-3">
                    {giftResponse.filter(status => status.status === 'pending').map((code) => (
                        <div key={code.gift_code} className="bg-[#0f1729] rounded-lg overflow-hidden flex flex-col items-center py-2 px-2 shadow-lg">
                            <div className="flex items-center gap-1 text-green-400 mb-0">
                                <span className="text-xs">✦</span>
                                <span className="text-xs font-bold">{code.plan_type} Plan</span>
                            </div>
                            <div className="w-20 h-20 relative animate-pulse">
                                <div className="absolute inset-0 bg-green-400/10 rounded-full blur-md"></div>
                                <img
                                    src={getPlanBadge(code.plan_type)}
                                    alt={code.plan_type}
                                    className="w-full h-full object-contain relative z-10"
                                />
                            </div>
                            <p className="text-white text-[10px] sm:text-xs mb-2">{code.duration_months} Month{code.duration_months > 1 ? 's' : ''}</p>

                            <div className="w-full overflow-hidden text-center mb-3">
                                <p className="text-[9px] text-green-400 truncate px-2">
                                    {`https://soundofmeme.com/redeem/success/${code.gift_code}`}
                                </p>
                            </div>

                            <div className="flex flex-col w-full gap-2 px-2">
                                <button
                                    onClick={() => handleCopy(`https://soundofmeme.com/redeem/success/${code.gift_code}`)}
                                    className="w-full flex items-center justify-center gap-1 sm:gap-1.5 text-[10px] bg-green-500 hover:bg-green-600 text-black font-medium py-1 sm:py-1.5 rounded-sm transition-colors"
                                >
                                    <Copy size={12} className="flex-shrink-0" />
                                    Copy Link
                                </button>

                                <button onClick={() => handleDownload(code.gift_code, code.plan_type, code.duration_months)}
                                    className="w-full flex items-center justify-center gap-1 sm:gap-1.5 text-[10px] border border-green-500/50 text-green-400 font-medium py-1 sm:py-1.5 rounded-sm hover:bg-green-500/10 transition-colors"
                                >
                                    <Download size={12} className="flex-shrink-0" />
                                    Download
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default ViewGiftCode
